import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import moment from "moment-timezone";
import {
  HoverNotificationStack,
  HoverNotification,
  HoverNotificationType,
} from "../components/layouts/HoverNotificationStack";

interface IncomingNotification {
  id?: string;
  notification_id?: string;
  user_id: string;
  symbol: string;
  stock_name: string;
  title: string;
  notification_page_url: string;
  type?: HoverNotificationType;
  notification_category?: string;
  notificationId?: string;
  category?: string;
}

export default function HoverNotificationStackWrapper() {
  const [notifications, setNotifications] = useState<HoverNotification[]>([]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const { pathname } = useRouter();

  useEffect(() => {
    if (pathname === "/notifications" && notifications.length > 0) {
      const timer = setTimeout(() => {
        clearAllNotifications();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [pathname, clearAllNotifications, notifications.length]);

  useEffect(() => {
    const handleNewNotification = (
      event: CustomEvent<IncomingNotification>,
    ) => {
      const data = event.detail;
      const generatedId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

      const newNotification: HoverNotification = {
        id: data.id || generatedId,
        type: data.type || "info",
        stockSymbol: data.symbol,
        stockName: data.stock_name,
        newsTitle: data.title,
        moreInfoLink: data.notification_page_url || "/notifications",
        notificationId: data.notificationId || data.notification_id,
        time: moment().format("h:mm A"),
        category: data.category || data.notification_category,
      };

      setNotifications((prev) => [...prev, newNotification]);
    };

    window.addEventListener(
      "onStackNotification",
      handleNewNotification as EventListener,
    );

    return () => {
      window.removeEventListener(
        "onStackNotification",
        handleNewNotification as EventListener,
      );
    };
  }, []);

  if (notifications.length === 0) return null;

  return (
    <div className="fixed right-4 bottom-4 w-80 max-h-[85vh] z-[9999] pointer-events-none flex flex-col justify-end">
      <HoverNotificationStack
        notifications={notifications}
        variant="compact"
        onRemove={dismissNotification}
        onClearAll={clearAllNotifications}
      />
    </div>
  );
}
